import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useEntityData } from '../../hooks/useEntityData';
import { formatAntisemitismTypeLabel, formatDate } from '../../utils/helpers';
import { parseScreenshots, separateImageAnalysis } from '../../utils/imageHelpers';
import '../../css/EntityFrame.css';

function EntityFrameScreenshot({ src, alt }) {
  const imgRef = useRef(null);
  const [fullyLoaded, setFullyLoaded] = useState(false);

  const markPresentationReady = useCallback(() => {
    const img = imgRef.current;
    if (!img || img.naturalWidth === 0) {
      setFullyLoaded(true);
      return;
    }
    const done = () => setFullyLoaded(true);
    if (typeof img.decode === 'function') {
      img.decode().then(done).catch(done);
    } else {
      done();
    }
  }, []);

  useLayoutEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      markPresentationReady();
    }
  }, [src, markPresentationReady]);

  return (
    <div className="entity-frame-media-image-shell">
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={`entity-frame-media-image ${fullyLoaded ? 'entity-frame-media-image--ready' : 'entity-frame-media-image--loading'}`}
        onLoad={markPresentationReady}
        onError={() => setFullyLoaded(true)}
        decoding="async"
      />
    </div>
  );
}

/** Figma node 473:4435 — "icons / search" in purple-light-01 pill; no raster assets. */
function EntityFrameEmptySearchIcon() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      className="entity-frame-empty-search-svg"
    >
      <circle cx="17" cy="17" r="7.25" stroke="#303030" strokeWidth="1.75" />
      <path d="M22.5 22.5L30 30" stroke="#303030" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

export const EntityFrame = ({
  entityId,
  searchFailed,
  hasNoResults,
  onClose,
  entities = [],
  onSelectEntity,
  totalCount = 0,
  currentPage = 1,
  pageSize = 25,
  handlePageChange,
  entitiesLoading = false,
  onReportPost
}) => {
  const navigatedFromScrollersRef = useRef(false);
  const { entityData, loading, error } = useEntityData(entityId);
  const currentIndex = entities.findIndex(e => String(e.EntityId) === String(entityId));
  const shouldHideNavigation = currentIndex === -1 && !navigatedFromScrollersRef.current;
  const entityFromList = currentIndex >= 0 ? entities[currentIndex] : null;

  const pendingScrollerNavigationRef = useRef(null);
  const navigationStartEntitiesRef = useRef(null);
  
  useEffect(() => {
    if (entitiesLoading) {
      return;
    }
    if (pendingScrollerNavigationRef.current) {
      if (entities === navigationStartEntitiesRef.current) {
        return;
      }

      if (entities.length > 0) {
        if (pendingScrollerNavigationRef.current === 'next-page-last') {
          const lastEntity = entities[entities.length - 1];
          if (lastEntity) {
            onSelectEntity && onSelectEntity(lastEntity.EntityId);
          }
        } else if (pendingScrollerNavigationRef.current === 'prev-page-first') {
          const firstEntity = entities[0];
          if (firstEntity) {
            onSelectEntity && onSelectEntity(firstEntity.EntityId);
          }
        }
      }
      
      pendingScrollerNavigationRef.current = null;
      navigationStartEntitiesRef.current = null;
    }
    if (currentIndex >= 0) {
      navigatedFromScrollersRef.current = false;
    }
  }, [currentPage, entities, currentIndex, onSelectEntity, entitiesLoading]);

  const handleNextWithPagination = async () => {
    navigatedFromScrollersRef.current = true;
    const nextIndex = currentIndex - 1;
    if (nextIndex >= 0 && entities[nextIndex]) {
      onSelectEntity && onSelectEntity(entities[nextIndex].EntityId);
    } else if (nextIndex < 0 && currentPage > 1 && handlePageChange) {
      pendingScrollerNavigationRef.current = 'next-page-last';
      navigationStartEntitiesRef.current = entities;
      await handlePageChange(currentPage - 1);
    }
  };

  const handlePrevWithPagination = async () => {
    navigatedFromScrollersRef.current = true;
    const prevIndex = currentIndex + 1;
    if (prevIndex < entities.length && entities[prevIndex]) {
      onSelectEntity && onSelectEntity(entities[prevIndex].EntityId);
    } else if (prevIndex >= entities.length && handlePageChange) {
      const totalPages = Math.ceil(totalCount / pageSize);
      if (currentPage < totalPages) {
        pendingScrollerNavigationRef.current = 'prev-page-first';
        navigationStartEntitiesRef.current = entities;
        await handlePageChange(currentPage + 1);
      }
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const isNextDisabled = currentIndex <= 0 && currentPage === 1;
  const isPrevDisabled = currentIndex >= entities.length - 1 && currentPage >= totalPages;
  const effectiveEntity = entityData?.entity || entityFromList;
  const tags = entityData?.tags || null;

  const isRemoved = !!effectiveEntity?.IsRemoved;

  const getDescriptionText = () => {
    const longText = effectiveEntity?.contentDesc || '';
    if (longText) {
      return longText;
    }
    const separated = separateImageAnalysis(effectiveEntity?.Text || '');
    return separated.postText || 'No description is available for this post.';
  };

  const screenshot = parseScreenshots(effectiveEntity?.Screenshots || [])[0]?.url || '';

  const renderEntityCard = () => {
    if (!effectiveEntity) return null;

    const uploadedOn = formatDate(effectiveEntity.CreatedDate) || 'N/A';
    const language = effectiveEntity.Language || 'N/A';

    return (
      <div className="entity-frame-design">
        <div className="entity-frame-header-row">
          <h2 className="entity-frame-author">@{effectiveEntity.AuthorName || 'unknown_user'}</h2>
          <span className={`entity-frame-status-pill ${isRemoved ? 'removed' : 'online'}`}>
            {isRemoved ? 'Removed' : 'Not Removed'}
          </span>
        </div>
        <div className="entity-frame-meta">
          <span>Uploaded On: {uploadedOn}</span>
          <span className="entity-frame-meta-dot">•</span>
          <span>Language: {language}</span>
        </div>
        <div className="entity-frame-main">
          <div className="entity-frame-media-card">
            {screenshot ? (
              <EntityFrameScreenshot key={screenshot} src={screenshot} alt="Post screenshot" />
            ) : (
              <div className="entity-frame-media-placeholder">No screenshot available</div>
            )}
          </div>
          <aside className="entity-frame-side-panel">
            <section>
              <h3>Post Data</h3>
              <div className="entity-frame-info-box">
                <div className="entity-frame-field-label">Country</div>
                <div className="entity-frame-field-value">{effectiveEntity.Country || 'N/A'}</div>
                <div className="entity-frame-field-label">Platform</div>
                <div className="entity-frame-field-value">{effectiveEntity.Source || 'N/A'}</div>
              </div>
            </section>
            <section>
              <h3>Classification</h3>
              <div className="entity-frame-info-box">
                <div className="entity-frame-field-label">Antisemitism Types</div>
                <div className="entity-frame-field-value">
                  {tags?.AntisemitismTypes?.length
                    ? tags.AntisemitismTypes.map(formatAntisemitismTypeLabel).join(', ')
                    : 'N/A'}
                </div>
                <div className="entity-frame-field-label">Violation of Policy</div>
                <div className="entity-frame-field-value entity-frame-field-danger">
                  {tags?.PolicyViolation || 'N/A'}
                </div>

              </div>
            </section>
            <button
              type="button"
              className="entity-frame-report-btn"
              onClick={() => {
                if (effectiveEntity?.Url) {
                  window.open(effectiveEntity.Url, '_blank', 'noopener,noreferrer');
                  onReportPost && onReportPost(effectiveEntity.EntityId);
                }
              }}
              disabled={!effectiveEntity?.Url}
            >
              Report Post
            </button>
          </aside>
        </div>
        <section className="entity-frame-description">
          <h3>Description</h3>
          <div className="entity-frame-description-box">{getDescriptionText()}</div>
        </section>
        {!shouldHideNavigation && (
          <div className="entity-frame-nav-bar">
            <button type="button" className="entity-frame-nav-btn" onClick={handleNextWithPagination} disabled={isNextDisabled}>
              ‹ Previous Post
            </button>
            <button type="button" className="entity-frame-nav-btn" onClick={handlePrevWithPagination} disabled={isPrevDisabled}>
              Next Post ›
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (!entityId) {
      if (searchFailed) {
        return (
          <div className="entity-frame-empty-state">
            <div className="entity-frame-empty-icon">
              <EntityFrameEmptySearchIcon />
            </div>
            <div className="entity-frame-empty-title">View Details</div>
            <div className="entity-frame-empty-subtitle">Entity not found. Try a different search.</div>
          </div>
        );
      } else if (hasNoResults) {
        return (
          <div className="entity-frame-empty-state">
            <div className="entity-frame-empty-icon">
              <EntityFrameEmptySearchIcon />
            </div>
            <div className="entity-frame-empty-title">View Details</div>
            <div className="entity-frame-empty-subtitle">No entities found for your current filters.</div>
          </div>
        );
      }
      return (
        <div className="entity-frame-empty-state">
          <div className="entity-frame-empty-icon">
            <EntityFrameEmptySearchIcon />
          </div>
          <div className="entity-frame-empty-title">View Details</div>
          <div className="entity-frame-empty-subtitle">Select a post from the list to view the full analysis.</div>
          <div className="entity-frame-empty-note">Use filters or search to narrow results.</div>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="entity-frame-loading-content" aria-busy="true" aria-label="Loading entity details">
          <div className="entities-loading-spinner">
            <div className="spinner" />
          </div>
        </div>
      );
    }

    if (error || (!entityData && entityId)) {
      return (
        <div className="entity-frame-placeholder entity-frame-placeholder-large">
          <span className="entity-frame-placeholder-emoji" role="img" aria-label="Error">❌</span>
          <div className="entity-frame-placeholder-text">Entity not found or failed to load.</div>
        </div>
      );
    }

    return renderEntityCard();
  };

  return (
    <div className="entity-frame">
      <div className="entity-frame-content">
        {renderContent()}
      </div>
    </div>
  );
};

export default EntityFrame;

